import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable, inject, signal } from "@angular/core";
import { EBField, EntityConfiguration, EntityDataModel, RejectReason, Section, SubSection } from "@shared/enitity-configuration.model";
import { CodeName } from "@shared/models/code-name.model";
import { EntityDataKYC, KYCTypes } from "@shared/models/entity-data-kyc.model";
import { ProductCategoryData } from "@shared/models/product-category.model";
import { DocumentCheckListDto } from "app/document-check-list/document-checklist-Dto";
import { EnvConfigService } from "app/env-config.service";
import { BehaviorSubject, Observable } from "rxjs";
import { FileDTO } from "./file-dto";
import { LMSDedupeResponseDTO } from "./lms-dedupe-dto.model";
import { PrimaryKYCDTO } from "./primary-kyc-dto.model";
import { ProductModel } from "./product.model";
import { SecondaryKYCDTO } from "./secondary-kyc-dto.model";
import { EntityDocumentChecklistDTO, ReportWithEntitiesDTO, SaveDocumentChecklistImportDTO } from "./widget-document-checklist/entity-document-checklist-dto-model";
import { BankAAResponse } from "./widget-static-components/bank-integrator/aggregator-bank-account.model";

@Injectable({
  providedIn: "root",
})
export class EntityDataService {
  _httpClient = inject(HttpClient);
  envConfig = inject(EnvConfigService);

  public $entityData: BehaviorSubject<EntityDataModel | null> = new BehaviorSubject<EntityDataModel | null>(null);

  public $childEntityData: BehaviorSubject<EntityDataModel | null> = new BehaviorSubject<EntityDataModel | null>(null);

  public $product = signal<ProductModel | null>(null);

  publishProduct(product: ProductModel) {
    this.$product.set(product);
  }

  publishEntityData(entityData: EntityDataModel) {
    this.$entityData.next(entityData);
  }

  publishChildEntityData(entityData: EntityDataModel) {
    this.$childEntityData.next(entityData);
  }

  clearEntityData() {
    this.$entityData.next(null);
  }

  clearChildEntityData() {
    this.$childEntityData.next(null);
  }

  constructor(private _http: HttpClient) { }

  createEntity(entityVersion: number, entityData: EntityDataModel, entityValue: string = undefined): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/create/${entityVersion}${entityValue ? `?entityValue=${entityValue}` : ""}`, entityData);
  }

  saveData(entityType: string, entityValue: string, entityVersion: number, data: any, entityDataId: string, widgetId?: string): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/save/${entityType}/${entityValue}/${entityVersion}?entityDataId=${entityDataId}${widgetId ? `&widgetId=${widgetId}` : ""}`,
      data,
    );
  }

  saveDataV2(data: any, widgetId: string, entityDataId?: string, parentWidgetId?: string, parentEntityDataId?: string, skipGroovy?: boolean): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/save/v2?widgetId=${widgetId}${entityDataId ? `&entityDataId=${entityDataId}` : ""}${parentWidgetId ? `&parentWidgetId=${parentWidgetId}` : ""}${parentEntityDataId ? `&parentEntityDataId=${parentEntityDataId}` : ""} ${skipGroovy ? `&skipGroovy=${skipGroovy}` : ""}`,
      data,
    );
  }

  saveKYCData(entityType: string, entityValue: string, data: EntityDataKYC): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/kyc/save/${entityType}/${entityValue}`, data);
  }

  submitEntity(enitityType: string, entityValue: string): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/submit/${enitityType}/${entityValue}`, {});
  }

  search(enitityType: string, productCategoryCode: string): Observable<EntityDataModel[]> {
    return this._httpClient.get<EntityDataModel[]>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/search?entityType=eq:${enitityType}&productCategory=eq:${productCategoryCode}`);
  }

  getEntityConfiguration(entityName: string, entityType: string, entityValue: string, parentWidgetKey?: string): Observable<EntityConfiguration> {
    return this._httpClient.get<EntityConfiguration>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/entityConfiguration?entityName=${entityName}&entityType=${entityType}&entityValue=${entityValue}${parentWidgetKey ? `&parentWidgetKey=${parentWidgetKey}` : ""}`,
    );
  }

  getChildEntityConfiguration(entityName: string, entityType: string, entityValue: string, entityDataId?: string): Observable<EntityConfiguration> {
    return this._httpClient.get<EntityConfiguration>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/entityConfiguration/child-entity?entityName=${entityName}&entityType=${entityType}&entityValue=${entityValue}${entityDataId ? `&childEntityDataId=${entityDataId}` : ""}`,
    );
  }

  getEntityConfigurationList(entityName: string[], entityType: string, entityValue: string): Observable<EntityConfiguration[]> {
    return this._httpClient.get<EntityConfiguration[]>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/entityConfiguration/list?entityName=${entityName}&entityType=${entityType}&entityValue=${entityValue}`,
    );
  }

  getKYCData(entityType: string, entityValue: string, kycType: KYCTypes): Observable<EntityDataKYC> {
    return this._httpClient.get<EntityDataKYC>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/kyc/${entityType}/${entityValue}?kycType=${kycType}`);
  }

  getEntityData(entityType: string, entityValue: string, baseEntityCode?: string): Observable<EntityDataModel> {
    return this._httpClient.get<EntityDataModel>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/fetch/${entityType}/${entityValue}${baseEntityCode ? `?baseEntityCode=${baseEntityCode}` : ""}`,
    );
  }

  getEntityDataById(entityDataId: string): Observable<EntityDataModel> {
    return this._httpClient.get<EntityDataModel>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/fetch/byId/${entityDataId}`);
  }

  getProductCategoryList(): Observable<ProductCategoryData> {
    return this._httpClient.get<ProductCategoryData>(`${this.envConfig.envConfig.APIBASEURL}/api/application/getProductCategoryList`);
  }
  uploadFile(fileData: FormData) {
    return this._httpClient.post<FileDTO>(`${this.envConfig.envConfig.APIBASEURL}/api/files/upload`, fileData);
  }

  getChildData(baseEntityCode: string, entityValue: string): Observable<EntityDataModel[]> {
    return this._httpClient.get<EntityDataModel[]>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/fetch/child-data?baseEntityCode=${baseEntityCode}&entityValue=${entityValue}`);
  }

  getProducts(productCategoryCode: string): Observable<ProductModel[]> {
    return this._httpClient.get<ProductModel[]>(`${this.envConfig.envConfig.APIBASEURL}/api/application/products/productCategoryCode/${productCategoryCode}`);
  }

  getBaseEntityByEntityTypeAndValue(entityType: string, entityValue: string): Observable<EntityDataModel> {
    return this._httpClient.get<EntityDataModel>(`/api/entity-data/base-entity/${entityType}/${entityValue}`);
  }

  validateAadhaarRemote(aadhaarNumber: string): Observable<HttpResponse<PrimaryKYCDTO>> {
    return this._httpClient.post<PrimaryKYCDTO>(
      "api/application/external/integration/aadharKYC/validate",
      aadhaarNumber,

      { headers: { "Content-Type": "text/plain" }, observe: "response" },
    );
  }

  validatePANRemote(panNumber: string): Observable<HttpResponse<SecondaryKYCDTO>> {
    return this._httpClient.post<SecondaryKYCDTO>("api/application/external/integration/panKYC/validate", panNumber, { observe: "response" });
  }

  getDocumentChecklist(): Observable<EntityDocumentChecklistDTO[]> {
    return this._httpClient.get<EntityDocumentChecklistDTO[]>("api/application/documentChecklist/search-all");
  }

  getDocumentChecklistForGenerate(entityType: string, entityValue: string, productCategory: string): Observable<EntityDocumentChecklistDTO[]> {
    return this._httpClient.get<EntityDocumentChecklistDTO[]>("api/application/documentChecklist/report-list", {
      params: { entityType, entityValue, productCategory },
    });
  }

  getReportsWithEntities(entityType: string, entityValue: string, productCategory?: string): Observable<ReportWithEntitiesDTO[]> {
    let params = new HttpParams().set("entityType", entityType).set("entityValue", entityValue);

    if (productCategory) {
      params = params.set("productCategory", productCategory);
    }

    return this._httpClient.get<ReportWithEntitiesDTO[]>(
      "api/application/report-with-entities",
      { params }
    );
  }
  uploadDocument(file: File, latitude?: number, longitude?: number): Observable<FileDTO> {
    const formData = new FormData();
    formData.append("file", file);

    if (latitude != null && longitude != null) {
      formData.append("latitude", latitude.toFixed(6));
      formData.append("longitude", longitude.toFixed(6));
    }

    return this._httpClient.post<FileDTO>(`${this.envConfig.envConfig.APIBASEURL}/api/files/upload`, formData);
  }

  saveDocumentChecklist(documentsList: SaveDocumentChecklistImportDTO[], entityType: string, entityValue: string, entityDataId: string, productCategoryCode: string): Observable<any> {
    // const params = new HttpParams().set("entityType", entityType).set("entityValue", entityValue);

    return this._httpClient.post(`api/application/entity-documents/save/${productCategoryCode}?entityType=${entityType}&entityValue=${entityValue}&entityDataId=${entityDataId}`, documentsList);
  }

  initiateEKYC(aadharNumber: string): Observable<string> {
    return this._httpClient.post<string>(`api/application/external/integration/aadharKYC/ekyc/initiate`, aadharNumber, {
      responseType: "text" as "json",
    });
  }

  validateEKYC(formdata: any): Observable<any> {
    return this._httpClient.post<any>(`api/application/external/integration/aadharKYC/ekyc/validate`, formdata);
  }

  validateOCR(formData: any): Observable<any> {
    return this._httpClient.post<any>(`api/application/external/integration/aadharKYC/ocr/validate`, formData);
  }

  validateVoterIdOCR(formData: any): Observable<any> {
    return this._httpClient.post<any>(`api/application/external/integration/voterId/ocr/validate`, formData);
  }

  getBranchSetCodes(): Observable<CodeName[]> {
    return this._httpClient.get<CodeName[]>("/api/user-management/branch-set");
  }

  searches(entityType: string, productCategoryCode: string, dynamicSearchData: any, pageIndex = 0, pageSize): Observable<EntityDataModel[]> {
    let params = new HttpParams().set("entityType", `eq:${entityType}`).set("productCategory", `eq:${productCategoryCode}`).set("page", pageIndex).set("size", pageSize);

    if (dynamicSearchData !== null) {
      Object.entries(dynamicSearchData).forEach(([key, value]) => {
        params = params.set(key, `eq:${value}`);
      });
    }

    return this._httpClient.get<EntityDataModel[]>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/search`, { params });
  }

  addRejectReason(entityType: string, entityTypeValue: string, data: RejectReason[]): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(`/api/entity-data/reject-reasons/${entityType}/${entityTypeValue}`, data);
  }
  getFinancialData() {
    return this._httpClient.get<any>("/api/application/external/integration/financial-data");
  }

  collateralVerification(data: any, entityType: String, entityValue: string): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(`api/entity-data/collateral-verification/${entityType}/${entityValue}`, data);
  }
  collateralStatusUpdate(entityType: string, entityValue: string): Observable<string> {
    return this._httpClient.put(`/api/entity-data/collateral-verification/updated-status/${entityType}/${entityValue}`, null, { responseType: "text" });
  }

  saveGstData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/gst-data/${entityType}/${entityValue}`, data);
  }

  saveItReturnsData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/it-returns/${entityType}/${entityValue}`, data);
  }
  savePurchaseBillsData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/purchase-bills/${entityType}/${entityValue}`, data);
  }
  saveSalesBillsData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/sales-bills/${entityType}/${entityValue}`, data);
  }
  saveLiabilityData(entityType: string, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/save/liability/${entityType}/${entityValue}`, data);
  }

  updateLiabilityData(entityType: string, entityValue: string, itemIndex: number, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/update/${entityType}/${entityValue}/${itemIndex}`, data);
  }
  removeLiabilityData(entityType: string, entityValue: string, itemIndex: number): Observable<any> {
    return this._httpClient.delete<any>(`api/entity-data/delete/${entityType}/${entityValue}/${itemIndex}`);
  }

  getLiabilityData(entityType: String, entityValue: string): Observable<any> {
    return this._httpClient.get<any>(`api/entity-data/liability/${entityType}/${entityValue}`);
  }

  calculateFoir(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`/api/entity-data/foir/${entityType}/${entityValue}`, data);
  }

  getDocumentChecklistByProductCodeAndEntityType(productCode: string, entityType?: string, entityValue?: string): Observable<DocumentCheckListDto[]> {
    let params = new HttpParams();
    if (entityType) {
      params = params.set("entityType", entityType);
    }
    if (entityValue) {
      params = params.set("entityValue", entityValue);
    }
    console.log(productCode);
    return this._httpClient.get<DocumentCheckListDto[]>(`/api/application/documentChecklist/productCode/${productCode}`, { params });
  }

  getSavedDocumentChecklist(entityType: string, entityValue: string): Observable<any[]> {
    const params = new HttpParams().set("entityType", entityType).set("entityValue", entityValue);

    return this._httpClient.get<any[]>("/api/application/documentChecklist/saved", { params });
  }
  getSavedDocumentCheckListByEntityId(entityId: string): Observable<any[]> {
    return this._httpClient.get<any[]>(`/api/application/entity-documents/${entityId}`);
  }

  calculateEMI(loadAmount: number, interestRate: number, tenure: number): Observable<any> {
    return this._httpClient.get<any>(`api/entity-data/loan-calculation/calculate-emi?loanAmount=${loadAmount}&annualInterestRate=${interestRate}&tenureMonths=${tenure}`);
  }

  downloadReport(documentChecklistId: number, entityId: string) {
    return this._httpClient.post(`api/report-queue/reports/download/${documentChecklistId}/${entityId}`, null, {
      responseType: "blob",
      observe: "response",
    });
  }

  apiForOptions(url: string, pathParams?: Record<string, string | number>): Observable<[]> {
    if (pathParams) {
      Object.keys(pathParams).forEach((key) => {
        const value = encodeURIComponent(pathParams[key]);
        url = url.replace(`\${${key}}`, value);
      });
    }
    return this._httpClient.get<[]>(`${this.envConfig.envConfig.APIBASEURL}/${url}`);
  }

  getFileStream(fileId: number) {
    const url = `${this.envConfig.envConfig.APIBASEURL}/api/files/stream/${fileId}`;

    return this._httpClient.get(url, {
      responseType: "blob",
      observe: "response",
    });
  }

  saveAgriIncomeData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/agriIncome/${entityType}/${entityValue}`, data);
  }
  saveDairyIncomeData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/dairyIncome/${entityType}/${entityValue}`, data);
  }

  saveSalaryWagesRentData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/salary-wages-rental/${entityType}/${entityValue}`, data);
  }

  deleteSalaryWagesRentData(entityType: String, entityValue: string, type: string, itemIndex: number): Observable<any> {
    return this._httpClient.delete<any>(`api/entity-data/delete-salary-wages-rental/${entityType}/${entityValue}/${type}/${itemIndex}`);
  }
  // API to verify application document.
  verifyDocument(documentsList: SaveDocumentChecklistImportDTO, entityType: string, entityValue: string, entityDataId: string): Observable<any> {
    return this._httpClient.post(`api/application/entity-documents/verify/${entityDataId}?entityType=${entityType}&entityValue=${entityValue}`, documentsList);
  }
  // API to reject application document.
  rejectDocument(documentsList: SaveDocumentChecklistImportDTO, entityType: string, entityValue: string, entityDataId: string): Observable<any> {
    return this._httpClient.post(`api/application/entity-documents/reject/${entityDataId}?entityType=${entityType}&entityValue=${entityValue}`, documentsList);
  }

  validatePanOCR(formData: any): Observable<any> {
    return this._httpClient.post<any>(`api/application/external/integration/panKYC/ocr/validate`, formData);
  }

  initiateDigiLocker(entityType: string, entityValue: string, data: any) {
    return this._httpClient.post<any>(`api/external/digilocker/initiate/${entityType}/${entityValue}`, data, { observe: "response" });
  }

  getkycResponseData(widgetId: string, parentWidgetId: string, entityDataId: string, entityValue: string) {
    return this._httpClient.get<any>(`api/external/digilocker/download/${widgetId}/${parentWidgetId}/${entityDataId}/${entityValue}`, { observe: "response" });
  }

  getLoanRequestedOverview(entityType: string, entityValue: string) {
    return this._httpClient.get<any>(`api/entity-data/overview/loanrequested/${entityType}/${entityValue}`, { observe: "response" });
  }

  getLoanProposedOverview(entityType: string, entityValue: string) {
    return this._httpClient.get<any>(`api/entity-data/overview/loanproposed/${entityType}/${entityValue}`, { observe: "response" });
  }
  getLtvOverview(entityType: string, entityValue: string) {
    return this._httpClient.get<any>(`api/entity-data/overview/ltv/${entityType}/${entityValue}`, { observe: "response" });
  }

  getFoirOverview(entityType: string, entityValue: string) {
    return this._httpClient.get<any>(`api/entity-data/overview/foir/${entityType}/${entityValue}`, { observe: "response" });
  }

  getPreCheck(entityType: string, entityValue: string, widgetId?: string): Observable<any> {
    let params = new HttpParams();
    if (entityType) {
      params = params.set("widgetId", widgetId);
    }
    return this._httpClient.get<any>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/precheck/${entityType}/${entityValue}`, { params });
  }

  executeDocumentList(documentChecklistId: number, entityId: string) {
    return this._httpClient.post<any>(
      `api/report-queue`,
      null, // body is null because your API uses only @RequestParam
      {
        params: {
          documentChecklistId: documentChecklistId,
          entityId: entityId,
        },
      },
    );
  }

  saveVerifiedData(entityDataId: String, entityType: String, entityValue: string, index: number, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/salary-wages-rental/${entityDataId}/${entityType}/${entityValue}/${index}`, data);
  }

  saveBusinessCashAssetsData(entityType: String, entityValue: string, data: any): Observable<any> {
    return this._httpClient.post<any>(`api/entity-data/business-cash-assessment/${entityType}/${entityValue}`, data);
  }
  getAllRelatedParties(entityTypeValue: string): Observable<any> {
    return this._httpClient.get<any>(`api/entity-data/relatedParties/${entityTypeValue}`);
  }
  getAllRelatedPartiesWithOthers(entityTypeValue: string): Observable<any> {
    return this._httpClient.get<any>(`api/entity-data/relatedParties/withothers/${entityTypeValue}`);
  }
  getAllFields(config: EntityConfiguration): EBField[] {
    const fields: EBField[] = [];

    function traverse(obj: any): void {
      if (obj?.fields?.length) {
        fields.push(...obj.fields);
      }
      if (obj?.sections?.length) {
        obj.sections.forEach((section: Section) => traverse(section));
      }
      if (obj?.subSections?.length) {
        obj.subSections.forEach((sub: SubSection) => traverse(sub));
      }
    }

    traverse(config);
    return fields;
  }

  getFieldsValue(config: EntityConfiguration): Map<string, string> {
    const fields = this.getAllFields(config);
    const fieldValues = new Map<string, string>();
    fields.forEach((field) => {
      fieldValues.set(field.fieldMeta.fieldName, field.value);
    });
    return fieldValues;
  }
  toIndianShortNumber(value: number | string): string {
    const num = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(num)) return "";

    if (num >= 10000000) {
      // Crores
      return (num / 10000000).toFixed(2).replace(/\.00$/, "") + "Cr";
    } else if (num >= 100000) {
      // Lakhs
      return (num / 100000).toFixed(2).replace(/\.00$/, "") + "L";
    } else if (num >= 1000) {
      // Thousands
      return (num / 1000).toFixed(2).replace(/\.00$/, "") + "K";
    } else {
      // Below thousand - no suffix
      return num.toString().replace(/\.00$/, "");
    }
  }

  getEntityConfigurationV2(widgetId: string, entityDataId?: string, parentWidgetId?: string): Observable<EntityConfiguration> {
    return this._httpClient.get<EntityConfiguration>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/entityConfiguration/v2?widgetId=${widgetId}${entityDataId ? `&entityDataId=${entityDataId}` : ""}${parentWidgetId ? `&parentWidgetId=${parentWidgetId}` : ""}`,
    );
  }

  getLmsCustomerDedupe(entityType: string, entityValue: string, entityId: string, baseEntityCode: string): Observable<LMSDedupeResponseDTO> {
    return this._httpClient.post<LMSDedupeResponseDTO>(`api/lms/dedupe/${entityType}/${entityValue}/${entityId}/${baseEntityCode}`, { observe: "response" });
  }

  verifyPanDigitap(entityType: string, entityValue: string, panNumber: string, subEntityTypes: string): Observable<HttpResponse<SecondaryKYCDTO>> {
    return this._httpClient.post<any>(`api/application/pan-kyc/${entityType}/${entityValue}/${panNumber}/${subEntityTypes}`, { observe: "response" });
  }

  verifyVoterDigitap(entityType: string, entityValue: string, voterIdNumber: string, subEntityTypes: string): Observable<HttpResponse<SecondaryKYCDTO>> {
    return this._httpClient.post<any>(`api/application/voter-kyc/${entityType}/${entityValue}/${voterIdNumber}/${subEntityTypes}`, { observe: "response" });
  }

  getAddressOverview(entityType: string, entityValue: string, groovyName: string) {
    return this._httpClient.get<any>(`api/entity-data/overview/genericGroovyForOverview/${entityType}/${entityValue}/${groovyName}`, { observe: "response" });
  }

  getAllRelatedPartiesForMultipleApplicants(entityTypeValue: string): Observable<any> {
    return this._httpClient.get<any>(`api/entity-data/relatedParties/${entityTypeValue}`);
  }

  getAllBusiness(entityTypeValue: string): Observable<any> {
    return this._httpClient.get<any>(`api/entity-data/business/${entityTypeValue}`);
  }

  getDigiLockerTransactionByEntityDataId(entityDataId: string): Observable<any> {
    return this._httpClient.get<any>(`${this.envConfig.envConfig.APIBASEURL}/api/external/digilocker/entity/${entityDataId}`);
  }

  createDataForMultipleApplicant(parentWidgetId: string, parentEntityDataId: string, widgetId: string): Observable<EntityDataModel> {
    return this._httpClient.post<EntityDataModel>(
      `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/multiple/create/v2?widgetId=${widgetId}${parentWidgetId ? `&parentWidgetId=${parentWidgetId}` : ""}${parentEntityDataId ? `&parentEntityDataId=${parentEntityDataId}` : ""}`,
      null,
    );
  }

  getLoanScore(entityType: string, entityValue: string): Observable<any> {
    return this._httpClient.get<any>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/overview/score/${entityType}/${entityValue}`);
  }

  initiateEsign(entityType: string, entityValue: string, data: any, multiSigner: boolean): Observable<any> {
    return this._httpClient.post(`${this.envConfig.envConfig.APIBASEURL}/api/esign/initiateESign/${entityType}/${entityValue}?multiSigner=${multiSigner}`, data);
  }

  eSignReport(entityType: string, entityValue: string): Observable<any[]> {
    return this._httpClient.get<any[]>(`${this.envConfig.envConfig.APIBASEURL}/api/esign/uploaded`, {
      params: { entityType, entityValue }
    });
  }

  downloadESignDocument(documentId: string) {
    const url = `${this.envConfig.envConfig.APIBASEURL}/api/esign/document/download/${documentId}`;
    return this._httpClient.get(url, { responseType: "blob", observe: "response", });
  }

  getAggregatorData(entityDataId: string): Observable<BankAAResponse[]> {
    return this._httpClient.get<BankAAResponse[]>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/aggregator/data/${entityDataId}`);
  }

  switchApplicant(coApplicantId: string): Observable<any[]> {
    return this._httpClient.post<any[]>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/switch-applicant`, null, {
      params: { coApplicantId: coApplicantId }
    });
  }

  //   getEntityDocumentsByEntityDataId(entityId: string): Observable<any[]> {
  //   return this._httpClient.get<any[]>(`/api/application/entity-documents/${entityId}/with-checklist`);
  // }

  // getEntityDocumentChecklist(productCode: string, entityType: string, entityValue: string) {
  //     return this._httpClient.get<any[]>(`${this.envConfig.envConfig.APIBASEURL}/api/application/entityDocuments/documentChecklist?productCode=${productCode}&entityType=${entityType}&entityValue=${entityValue}`
  //     );
  // }

  getEntityDocumentsV2(entityDataId: string, entityType: string) {
    return this._httpClient.get<any[]>(`${this.envConfig.envConfig.APIBASEURL}/api/application/entity-documentsV2/${entityDataId}?entityType=${entityType}`);
  }

  createEntitySpecificDocument(payload: any) {
    return this._httpClient.post<any>(`${this.envConfig.envConfig.APIBASEURL}/api/application/entity-documents/entity-specific-documents`, payload);
  }

  updateEntityDocument(entityDocument: any): Observable<any> {
    return this._httpClient.put<any>(`${this.envConfig.envConfig.APIBASEURL}/api/application/entityDocuments`, entityDocument);
  }

  deleteEntityDocument(id: string): Observable<any> {
    return this._httpClient.delete<any>(`${this.envConfig.envConfig.APIBASEURL}/api/application/entityDocuments/${id}`);
}
deleteChildEntityData(entityDataId: string): Observable<any> {
  return this._httpClient.delete<any>(
    `${this.envConfig.envConfig.APIBASEURL}/api/entity-data/delete/${entityDataId}`,
    { observe: 'response' }
  );
}
refreshFoir(entityType: string,entityTypeValue: string): Observable<any> {
  return this._httpClient.post<any>(`${this.envConfig.envConfig.APIBASEURL}/api/entity-data/financial-parameters/refresh/${entityType}/${entityTypeValue}`,{});
}
getIncomeTemplateConfiguration(){
  return this._httpClient.get<any>(`${this.envConfig.envConfig.APIBASEURL}/api/master-data/fetch-income-template-configuration`);
}
}
